/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnInit, Component, Injector, Input, ViewChild } from '@angular/core';
import { Validators } from '@angular/forms';
import {
    type Command, type HttpResponse, type Rest, DMValidators, FormControl, FormGroup, FormSlideWindowAbstract, SelectComponent, UNPLACEHOLDER,
    UtilService,
} from '@dm/core';
import { SmartGuiService } from 'app/common/services';
import { type DataTurboClient, type DataTurboShare, type Dtree, type FileSystem } from 'app/common/types/mo';
import { type Observable, type Subscription, of } from 'rxjs';
import { finalize, flatMap, tap } from 'rxjs/operators';

import { DtreeService } from '../../../dtree/dtree.service';
import { FileSystemEntryE } from '../../../file-system/create-file-system/create-file-system.component';
import { type CreateDataTurboParams, type SmartguiDataTurboshareReceived } from '../../share';
import { DataTurboShareService } from '../data-turbo-share.service';
import { type ErrorMessage, createTypeE, DataTurboShareStrService } from '../data-turbo-share-str.service';

@Component({
    selector: 'app-common-create-data-turbo-share',
    templateUrl: './create-data-turbo-share.component.html',
    styleUrls: ['./create-data-turbo-share.component.scss'],
})
export class CreateDataTurboShareComponent extends FormSlideWindowAbstract implements OnInit {
    i18n = this.str.i18n;
    @Input() fileSystem: FileSystem = {};
    @Input() dtree: Dtree = {};
    @Input() vstoreId: string;
    @Input() vstoreName: string;
    @Input() title: string = this.i18n.title.createTitle;
    // 模板创建的共享
    @Input() createAsShare: DataTurboShare;
    // 是否支持智能GUI，默认支持
    @Input() supportSmartGui = true;

    @Input() copyFromStyle: string;

    @Input() copyFromShare: DataTurboShare;
    // 第一次copyfrom过来的是undefined
    @Input() copyFromPermissions: DataTurboClient[];

    @Input() entry: FileSystemEntryE;
    // 模板创建，但是需要更改文件系统 dree等
    @Input() needChangeParam: boolean;

    @ViewChild('fsSelect') fsSelect: SelectComponent;

    UNPLACEHOLDER = UNPLACEHOLDER;
    // 是否有Dtree读权限，没有则不能选择Dtree
    hasDtreePermission = this.permissionService.hasPermission('dtree_R');

    closeArgs = {
        refresh: false,
        data: null,
    };

    pathLoading = false;
    loading = false;

    vm: FormGroup;
    errorMessage: ErrorMessage;

    get vmValue(): any {
        return this.vm ? this.vm.value : null;
    }

    fileSystemRest: Rest;
    dtreeRest: Rest;

    private _share_path = '';

    set share_path(value) {
        this._share_path = value;
        // 更新是否有\
        this.dtreePathHasBackslash = this.str.isPathHasBackslash(this._share_path);
    }

    get share_path(): string {
        // 如果是copyfrom过来的,并且不需要修改参数，sharepath 就固定从copyfrom share中获取
        return this.copyFromStyle && !this.needChangeParam ? this.copyFromShare.sharePath : this._share_path;
    }

    // dtree path 中是否含有反斜杠
    dtreePathHasBackslash = false;
    // 手动选择的文件系统对应的租户名
    selectVstoreName = '';
    permissionList: DataTurboClient[] = [];
    allUserLoading = false;
    canFileSystemChange = true;
    canDtreeChange = true;
    showDtree = true;

    selectedFilesystem: FileSystem;

    advanced = false;
    // 上一次选中的文件系统所属租户的vstoreId
    preFsVstoreId: string;

    // dataTurbo智能推荐数据
    dataTurboSmartguiData: SmartguiDataTurboshareReceived = {};

    createFsTaskId: string;
    createFs$: Subscription;

    constructor(
        injector: Injector,
        public str: DataTurboShareStrService,
        private DataTurboService: DataTurboShareService,
        private dtreeService: DtreeService,
        private smartguiService: SmartGuiService,
        private utilService: UtilService,

    ) {
        super(injector);
    }

    ngOnInit(): void {
        if (!this.checkDepPermission(['file_system_R'])) {
            return;
        }
        // 如果是copyfrom过来的,并且不需要修改参数,则固定对应参数
        if (this.copyFromShare && !this.needChangeParam) {
            this.share_path = this.copyFromShare.sharePath;
            this.canFileSystemChange = false;
            this.showDtree = false;
        }
        // 第一次点开和模板创建需要查客户端数据
        const needGetUserData = this.copyFromStyle === createTypeE.copyFrom && !this.copyFromPermissions || this.createAsShare;
        if (needGetUserData) {
            this.getAllUsers(this.createAsShare);
        }
        if (this.vstoreId) {
            this.preFsVstoreId = this.vstoreId;
        }

        this.permissionList = this.copyFromPermissions ? [...this.copyFromPermissions] : [];
        this.entryHandler();
        this.pageReady = true;
        this.windowVisible = true;
        this.fileSystemRest = this.DataTurboService.getBatchFSRest(this.vstoreId);

        super.ngOnInit();
    }

    getAllUsers(share: DataTurboShare): void {
        this.allUserLoading = true;
        this.DataTurboService.getUserBatch(share.vstoreId, share.ID)
            .pipe(
                tap((results: DataTurboClient[]) => {
                    const datas: DataTurboClient[] = [...results];
                    this.permissionList = datas;
                }),
                finalize(() => {
                    this.allUserLoading = false;
                }),
            ).subscribe();
    }

    /**
     * 获取DataTurbo智能推荐数据
     */
    getDataTurboSmartguiData(): void {
        if (!this.smartguiService.switch) {
            return;
        }

        // 添加忙等效果
        this.showLoading();
        this.DataTurboService.getSmartguiDataTurbo(this.vstoreId)
            .pipe(
                flatMap((data: FileSystem) => {
                    this.dataTurboSmartguiData = {
                        ...this.dataTurboSmartguiData,
                        ...data,
                    };
                    this.vm.get('NAME').setValue(this.dataTurboSmartguiData.NAME[0]);
                    if (this.dataTurboSmartguiData && this.dataTurboSmartguiData.filesystem) {
                        return this.DataTurboService.getFSOnName(this.dataTurboSmartguiData.filesystem);
                    } else {
                        return of(null);
                    }
                }),
                finalize(() => this.hideLoading()),
            )
            .subscribe((resp: FileSystem[] | null) => {
                if (resp && this.fileSystem && !this.fileSystem.NAME) {
                    const [item] = resp;
                    this.fsSelect.setFixedOption({
                        value: item && item.ID,
                        label: item && item.NAME,
                        data: item,
                    });
                }
            });
    }

    /**
     * 入口的判断和处理
     */
    entryHandler(): void {
        // 从文件系统或dtree入口进来,会传入文件系统，此时不允许修改文件系统和dtree
        if (!this.fileSystem?.NAME) {
            return;
        }

        this.share_path = this.str.getPath(this.fileSystem.NAME);
        this.canFileSystemChange = false;
        this.selectedFilesystem = this.fileSystem;

        if (this.dtree?.NAME) {
            this.pathLoading = true;
            this.canDtreeChange = false;
            this.DataTurboService.getDtree(this.dtree.ID, this.vstoreId).pipe(
                finalize(() => {
                    this.pathLoading = false;
                }),
            ).subscribe((data: Dtree) => {
                this.share_path = this.dtreeService.getDtreeFullPath(this.fileSystem, data);
            });
        } else {
            // 未传入有效Dtree说明是从文件系统详情进入，直接不显示Dtree
            this.showDtree = false;
        }
    }

    /**
     * 复写modify方法，传入文件系统时，需要直接true 让buldcmd执行
     */
    isModified(): boolean {
        if (this.fileSystem?.NAME) {
            return true;
        }
        return this.vm.valueChanged;
    }

    initVm(): void {
        // 初始化默认数据
        const defaultShare = {
            fsId: this.selectedFilesystem,
            description: this.copyFromStyle ? this.copyFromShare.description : '',
        };
        // 更新模板创建的数据
        if (this.createAsShare) {
            defaultShare.description = this.createAsShare.description;
        }

        this.vm = new FormGroup({
            filesystem: new FormControl(defaultShare.fsId, [
                Validators.required,
            ]),
            dtree: new FormControl(null, [
            ]),
            description: new FormControl(this.utilService.decodeHTML(defaultShare.description), [
                DMValidators.rangeByteLength(0, 255),
            ]),
        }, { updateOn: 'blur' });

        this.errorMessage = {
            filesystem: {
                required: this.str.requiredErr(this.i18n.label.fileSystem),
            },
            description: {
                rangeByteLength: this.str.rangeByteLengthErr(0, 255),
            },
        };
    }

    /**
     * 收集copyfromm页面需要的信息
     */
    collectionDataTurboAndUsers(): { share: DataTurboShare; permissions: DataTurboClient[] } {
        const share = this.colletCreateParams();
        return { share, permissions: this.permissionList };
    }


    colletCreateParams(): CreateDataTurboParams {
        const vmValue = this.vmValue;
        const share: CreateDataTurboParams = {
            sharePath: this.share_path,
            fsId: this.canFileSystemChange ? vmValue.filesystem : this.selectedFilesystem.ID,
            description: vmValue.description,
            vstoreId: this.vstoreId,
            dtreeId: vmValue.dtree,
        };
        return share;
    }

    buildCmds(): Command[] {
        const cmds: Command[] = [];
        if (this.copyFromStyle && !this.needChangeParam) {
            this.closeArgs.data = this.collectionDataTurboAndUsers();
        } else {
            cmds.push(this.buildDataTurboTask());
        }
        return cmds;
    }

    /**
     * 选择文件系统后，需要更新dtree和sharepath
     */
    onChooseFileSystem($event): void {
        if (!$event.value) {
            this.share_path = '';
            return;
        }
        this.share_path = this.str.getPath($event.option.data.NAME);

        this.selectedFilesystem = $event.option.data;
        if (this.hasDtreePermission) {
            this.dtreeRest = this.DataTurboService.getBatchDtreeRest(this.selectedFilesystem);
        }
        // 清空dtree
        this.vm.get('dtree').setValue(null);

        const currentFsVstoreId = this.selectedFilesystem.vstoreId;

        // 如果切换的文件系统所属租户变更，清空已选权限列表
        if (currentFsVstoreId !== this.preFsVstoreId) {
            this.permissionList = [];
        }
    }

    /**
     * 选择dtree之后，需要更新sharepath
     */
    onChooseDtree($event): void {
        if (!$event.value) {
            this.share_path = this.str.getPath(this.selectedFilesystem.NAME);
            return;
        }
        // dtree的全路径是单个查的，这个时候要展示微观忙等
        this.pathLoading = true;
        this.DataTurboService.getDtree($event.option.data.ID, this.selectedFilesystem.vstoreId).pipe(
            finalize(() => {
                this.pathLoading = false;
            }),
        ).subscribe((data: Dtree) => {
            this.share_path = this.dtreeService.getDtreeFullPath(this.selectedFilesystem, data);
        });
    }

    /**
     * 当处于所有租户模式并点击了权限列表的添加按钮，未选择文件系统时(无租户信息)给出必选校验提示
     */
    onClickAddBtn(): void {
        if (!this.vstoreId) {
            this.vm.get('filesystem').markAsTouched();
        }
    }

    /**
     * 构建task任务参数整合
     */
    buildDataTurboTask(): Command {
        const params = this.colletCreateParams();
        const isNeedOmtask = Boolean(this.permissionList.length);
        const publicParams = {
            id: 'DataTurbo_share',
            name: this.i18n.rest.createDataTurbo,
            nameParams: [this.share_path],
        };

        if (isNeedOmtask) {
            const params2 = { share: { ...params }, auth: this.buildDataTurboClientParams() };
            return {
                ...publicParams,
                action: this.DataTurboService.createByOmtask(params.sharePath, params2, { restNameParams: [this.share_path] }),
            };
        }

        return {
            ...publicParams,
            action: this.DataTurboService.create(params),
        };
    }

    /**
     * 构建添加用户/用户组下发参数
     */
    buildDataTurboClientParams(): DataTurboClient[] {
        return this.permissionList.map(({ userName, permission }) => ({ userName, permission: String(permission) }));
    }

    /**
     * 复写exec
     */
    exec(): void {
        // 无权限添加，不走omtask
        if (!this.permissionList.length) {
            super.exec();
            return;
        }

        // 有权限添加，走omtask
        const cmds = this.buildCmds();
        if (cmds && cmds.length === 1) {
            this.loading = true;
            (cmds[0].action as Observable<HttpResponse>).pipe(finalize(() => {
                this.loading = false;
            })).subscribe(() => {
                this.closeArgs.refresh = true;
                this.hide();
            });
        } else {
            this.hide();
        }
    }

    /**
     * 复写ok函数
     */
    onOk(): void {
        if (this.copyFromShare && !this.needChangeParam) {
            const param = { permissions: this.permissionList, description: this.vm.value.description };
            this.closeArgs.data = param;
            this.hide();
            return;
        }
        if (this.pathLoading) {
            return;
        }
        super.onOk();
    }
}
